<template>
    <div class="form_flex_wrap">
        <div class="form_flex_title">
            <span class="title">{{ $t('work_schedule.popup_operation_settings.title_department_management') }}</span>
        </div>
        <div class="form_flex_cont">
            <table class="table2_default">
                <colgroup>
                    <col />
                    <col width="144" />
                    <col width="95" />
                </colgroup>

                <thead>
                    <tr>
                        <th>{{ $t('work_schedule.popup_operation_settings.label_department_name') }}</th>
                        <th>{{ $t('work_schedule.popup_operation_settings.label_change_order') }}</th>
                        <th>{{ $t('common.delete') }}</th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="(department, index) in items" :key="index">
                        <td>
                            <input type="text" ref="inputRefs" class="input_s w_full" v-model="department.title" :placeholder="$t('work_schedule.popup_operation_settings.label_department_name')" @focus="hideErrorText" />
                        </td>
                        <td>
                            <div class="btn_group">
                                <button class="btn btn_xs btn_lgray_line" :disabled="index === 0" @click="updateOrder('FIRST', index)">
                                    <v-icon>mdi-chevron-double-up</v-icon>
                                </button>
                                <button class="btn btn_xs btn_lgray_line" :disabled="index === 0" @click="updateOrder('PREVIOUS', index)">
                                    <v-icon>mdi-chevron-up</v-icon>
                                </button>
                                <button class="btn btn_xs btn_lgray_line" :disabled="index === items.length - 1" @click="updateOrder('NEXT', index)">
                                    <v-icon>mdi-chevron-down</v-icon>
                                </button>
                                <button class="btn btn_xs btn_lgray_line" :disabled="index === items.length - 1" @click="updateOrder('LAST', index)">
                                    <v-icon>mdi-chevron-double-down</v-icon>
                                </button>
                            </div>
                        </td>
                        <td class="txt_center">
                            <button class="btn btn_s btn_dgray" @click="removeItem(index)">－ {{ $t('common.delete') }}</button>
                        </td>
                    </tr>
                    <tr>
                        <td class="txt_center" colspan="3">
                            <button class="btn btn_s btn_blue_line mgl_auto" @click="action_AddItem">＋ {{ $t('common.add') }}</button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <small class="red_txt" v-if="isShowErrorText">{{ errorText }}</small>
        </div>
        <div class="btn_wrap">
            <button class="btn btn_l btn_blue mgl_auto" @click="action_Save" :disabled="isLoading">{{ $t('common.save') }}</button>
        </div>
    </div>
</template>

<script>
import { deepCopy } from '@/util/deep-copy'
import { mapActions } from 'vuex'
import ManagementMixin from '../ManagementMixin.js'
import { handleRefresh } from '@/v2/views/Main/V2ReservationManagement/sheet-refresh'
import { workerState, setSelectedDate } from '../../../worker-state'
import { useQueryClient } from '@tanstack/vue-query'
import { isApiErrorWithCode } from '@/apis/instances/check-api-response'
import { v2DepartmentFindQueryOptions } from '@/composables/queries/v2department/use-v2-department-find-query'
import { useV2DepartmentCreateMutation } from '@/composables/queries/v2department/use-v2-department-create-mutation'
import { useV2DepartmentUpdateMutation } from '@/composables/queries/v2department/use-v2-department-update-mutation'
import { useV2DepartmentRemoveMutation } from '@/composables/queries/v2department/use-v2-department-remove-mutation'
const moment = require('@/util/MPMoment')

export default {
    setup() {
        const queryClient = useQueryClient()
        const { mutateAsync: createDepartment } = useV2DepartmentCreateMutation()
        const { mutateAsync: updateDepartment } = useV2DepartmentUpdateMutation()
        const { mutateAsync: removeDepartment } = useV2DepartmentRemoveMutation()
        return { queryClient, createDepartment, updateDepartment, removeDepartment }
    },
    mixins: [ManagementMixin],
    props: {
        departments: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            worker: workerState,
        }
    },
    methods: {
        ...mapActions(['setToast']),
        validateDepartments() {
            if (this.items.some((dept) => !dept.title)) {
                this.errorText = this.$t('work_schedule.popup_operation_settings.error_department_name')
                this.isShowErrorText = true
                return false
            }

            if (new Set(this.items.map((dept) => dept.title)).size !== this.items.length) {
                this.errorText = this.$t('work_schedule.popup_operation_settings.error_same_department_name')
                this.isShowErrorText = true
                return false
            }

            if (JSON.stringify(this.items) === JSON.stringify(this.originalItems)) {
                this.setToast({
                    isShow: true,
                    text: this.$t('work_schedule.popup_operation_settings.error_no_change')
                })
                return false
            }

            return true
        },

        // MARK: - Network
        async handleCreateDepartments() {
            let createTasks = this.items.filter((dept) => !dept._id)

            for (let i = 0; i < createTasks.length; i++) {
                let department = createTasks[i]
                try {
                    const created = await this.createDepartment({
                        title: department.title,
                        hospitalID: this.hospital.selectedHospital._id,
                        sortNum: department.sortNum
                    })
                    await this.updateDepartment({
                        _id: created._id,
                        sortNum: department.sortNum
                    })
                } catch (err) {
                    // 기존 동작 미러링: 봉투 code != 0 이면 로그만 남기고 다음 항목 진행
                    if (!isApiErrorWithCode(err)) throw err
                    console.log(err)
                }
            }

            return Promise.all(createTasks)
        },
        async handleUpdateDepartments() {
            let updateTasks = this.items
                .filter((dept) => dept._id && !this.originalItems.some((orig) => orig._id === dept._id && orig.title === dept.title && orig.sortNum === dept.sortNum))
                .map((dept) =>
                    this.updateDepartment({ _id: dept._id, title: dept.title, sortNum: dept.sortNum }).catch((err) => {
                        // 기존 동작 미러링: 봉투 에러는 무시하고 저장 성공 처리
                        if (!isApiErrorWithCode(err)) throw err
                    })
                )

            return Promise.all(updateTasks)
        },
        async handleRemoveDepartments() {
            let currentIds = this.items.map((dept) => dept._id)
            let deleteTasks = this.originalItems
                .filter((dept) => dept._id && !currentIds.includes(dept._id))
                .map((dept) =>
                    this.removeDepartment({ _id: dept._id }).catch((err) => {
                        // 기존 동작 미러링: 봉투 에러는 무시하고 저장 성공 처리
                        if (!isApiErrorWithCode(err)) throw err
                    })
                )

            return Promise.all(deleteTasks)
        },
        // MARK: - Network
        async requestFetchDepartmentList() {
            try {
                let hospitalID = this.hospital.selectedHospital._id
                let result = await this.queryClient.fetchQuery(v2DepartmentFindQueryOptions({
                    hospitalID,
                    isRemoved: false,
                    way: 1,
                    key: 'sortNum'
                }))

                this.$emit('updateDepartments', result ?? [])

                let departments = JSON.parse(JSON.stringify(result ?? []))

                this.items =
                    departments.length > 0
                        ? deepCopy(departments)
                        : Array.from({ length: departments.length }, () => ({ title: '', hospitalID: this.hospital.selectedHospital._id }))

                this.originalItems = deepCopy(this.items)
            } catch (err) {
                console.error('Error fetching department list:', err)
            }
        },
        // MARK: - Actions
        action_AddItem() {
            this.items.push({ title: '' })
            this.$nextTick(() => {
                let inputs = this.$refs.inputRefs
                if (inputs && inputs.length > 0) {
                    inputs[inputs.length - 1].focus()
                }
            })
        },
        async action_Save() {
            if (!this.validateDepartments()) return

            try {
                this.isLoading = true
                await Promise.all([this.handleCreateDepartments(), this.handleUpdateDepartments(), this.handleRemoveDepartments()])

                // Department 리스트 Refetch
                await this.requestFetchDepartmentList()
                handleRefresh()
                setSelectedDate(moment(this.worker.selectedDate))
                // 구 ManagingDataController 재주입 제거 — 부서 mutation invalidation 이
                // useManagingWorkers 구독을 자동 재조회시킨다
                this.$emit('updateDepartments', JSON.parse(JSON.stringify(this.items)))
                this.setToast({
                    isShow: true,
                    text: this.$t('common.label_save_success')
                })
            } catch (err) {
                console.error('Error processing department updates:', err)
                this.setToast({
                    isShow: true,
                    text: this.$t('common.label_save_error')
                })
            } finally {
                this.isLoading = false
            }
        }
    },
    mounted() {
        this.requestFetchDepartmentList()
    }
}
</script>

<style lang="scss" scoped></style>
